
import { useEffect } from 'react';

const SITE_NAME = 'Business Capital Guide';
const SITE_URL = 'https://businesscapitalguide.net';
const DEFAULT_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/f2fe758374db1567219a05f3ac8f8731.jpg';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  schema?: object;
  ogType?: string;
  ogImage?: string;
  ogImageAlt?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleSection?: string;
  noindex?: boolean;
}

function setMeta(attr: string, attrValue: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${attrValue}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: string, attrValue: string) {
  const el = document.querySelector(`meta[${attr}="${attrValue}"]`);
  if (el) el.remove();
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  schema,
  ogType = 'website',
  ogImage,
  ogImageAlt,
  articlePublishedTime,
  articleModifiedTime,
  articleSection,
  noindex,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    // Basic meta
    setMeta('name', 'description', description);
    if (keywords) {
      setMeta('name', 'keywords', keywords);
    }

    // Canonical
    if (canonical) {
      let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.href = canonical;
    }

    // Robots
    if (noindex) {
      setMeta('name', 'robots', 'noindex, nofollow');
    }

    // ── Open Graph ──
    const resolvedImage = ogImage || DEFAULT_OG_IMAGE;
    const resolvedAlt = ogImageAlt || title;

    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', canonical || window.location.href);
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:locale', 'en_US');
    setMeta('property', 'og:image', resolvedImage);
    setMeta('property', 'og:image:secure_url', resolvedImage);
    setMeta('property', 'og:image:type', 'image/jpeg');
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:image:alt', resolvedAlt);

    // Article-specific OG tags
    if (ogType === 'article') {
      if (articlePublishedTime) {
        setMeta('property', 'article:published_time', articlePublishedTime);
      }
      if (articleModifiedTime) {
        setMeta('property', 'article:modified_time', articleModifiedTime);
      }
      if (articleSection) {
        setMeta('property', 'article:section', articleSection);
      }
      setMeta('property', 'article:author', SITE_URL);
    }

    // ── Twitter Card ──
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:site', '@BizCapitalGuide');
    setMeta('name', 'twitter:creator', '@BizCapitalGuide');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', resolvedImage);
    setMeta('name', 'twitter:image:alt', resolvedAlt);

    // Schema
    if (schema) {
      const existingSchema = document.querySelector('script[data-schema="seo-head"]');
      if (existingSchema) existingSchema.remove();
      const schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-schema', 'seo-head');
      schemaScript.textContent = JSON.stringify(schema);
      document.head.appendChild(schemaScript);
    }

    return () => {
      // Clean up article-specific tags on unmount
      if (ogType === 'article') {
        removeMeta('property', 'article:published_time');
        removeMeta('property', 'article:modified_time');
        removeMeta('property', 'article:section');
        removeMeta('property', 'article:author');
      }
      const schemaEl = document.querySelector('script[data-schema="seo-head"]');
      if (schemaEl) schemaEl.remove();
    };
  }, [title, description, keywords, canonical, schema, ogType, ogImage, ogImageAlt, articlePublishedTime, articleModifiedTime, articleSection, noindex]);

  return null;
}
